/**
 * Data transformation utilities for Risk Calculator
 * Shared between RiskCalculator.jsx and ResultsScreen.jsx
 */

/**
 * Transform API response into ResultRow objects
 * Used when displaying risk calculation results
 *
 * @param {Array} apiData - Raw API response array
 * @param {Object} formData - Form selections { cultures, treatmentType, applicationTechnique }
 * @param {Object} enrichedData - Enriched data with labels { cultures, cultureChildren, treatmentType, applicationTechnique, products }
 * @returns {Array} Array of ResultRow objects
 */
export function transformApiResponseToResultRows(
  apiData,
  formData,
  enrichedData
) {
  const { cultures, treatmentType, applicationTechnique } = formData;

  const data = cultures.map((cultureId, index) => {
    const apiResult = apiData[index] || {};
    // Get culture info from enriched data (check both parents and children)
    const cultureInfo = enrichedData.cultures?.find(
      (c) => c.id === cultureId
    ) ||
      enrichedData.cultureChildren?.find((c) => c.id === cultureId) || {
        id: cultureId,
        name: cultureId,
      };

    return {
      id: `${cultureId}-${Date.now()}-${index}`, // Unique ID for row
      culture: {
        id: cultureId,
        name:
          cultureInfo.title ||
          cultureInfo.label ||
          cultureInfo.name ||
          cultureId,
      },
      treatmentType: {
        id: treatmentType,
        name: enrichedData.treatmentType?.name || treatmentType,
      },
      applicationTechnique: {
        id: applicationTechnique,
        name: enrichedData.applicationTechnique?.name || applicationTechnique,
      },
      products: (enrichedData.products || []).map((p) => ({
        id: p.id,
        name: p.name,
        approvalNumber: p.approvalNumber || '',
        dose: p.dose,
        unit: {
          id: p.unit,
          name: p.unitName,
        },
        activeSubstances: p.activeSubstances || [],
        listedDosages: p.listedDosages || null,
        labelUrl: p.labelUrl || null,
      })),
      // Nested symbols structure like activeSubstance table
      symbols: {
        health: {
          acute_toxicity_symbol: apiResult.symbols?.health?.acute_toxicity,
          long_term_effect_symbol: apiResult.symbols?.health?.long_term_effect,
        },
        environment: {
          fishes_symbol: apiResult.symbols?.environment?.fishes,
          birds_symbol: apiResult.symbols?.environment?.birds,
          bees_symbol: apiResult.symbols?.environment?.bees,
          persistence_symbol: apiResult.symbols?.environment?.persistence,
          leaching_symbol: apiResult.symbols?.environment?.leaching,
        },
      },
      // Keep original results for IRS/IRE values
      results: {
        health_risk_index: apiResult.health_risk_index,
        environment_risk_index: apiResult.environment_risk_index,
      },
      createdAt: Date.now(),
    };
  });
  return data;
}

/**
 * Format cascading select option with approval number
 * Used in ProductCascadingSelect for option display
 *
 * @param {Object} opt - Raw option from API
 * @param {string} type - Type of option ('product' | 'unit')
 * @returns {Object} Formatted option for Select component
 */
export function formatCascadingOption(opt, type = 'product') {
  if (type === 'product') {
    // Format label as "Product name (approval_number)" if approval_number exists
    const label = opt.approval_number
      ? `${opt.name} (${opt.approval_number})`
      : opt.name;

    return {
      label,
      value: opt.id,
      count: opt.count,
      doses: opt.doses || [],
      approval_number: opt.approval_number || '',
      name: opt.name,
      rawData: opt,
    };
  }

  // Default format for units and other types
  return {
    label: opt.name,
    value: opt.id,
    count: opt.count,
    rawData: opt,
  };
}

/**
 * Build enriched product object for storage
 * Used when saving product data with unit names
 *
 * @param {Object} row - Row data with product info
 * @returns {Object} Enriched product object
 */
export function buildEnrichedProduct(row) {
  // Keep sageDoses as array for re-initialization
  const sageDoses = row.sageDoses || [];
  return {
    id: row.id,
    name: row.name,
    approvalNumber: row.approvalNumber || '',
    dose: row.dose,
    unit: row.unit,
    unitName: row.unitName || '',
    // Store original array for re-initialization
    sageDoses: sageDoses,
    // Formatted string for results table display
    listedDosages: sageDoses.length > 0 ? sageDoses.join(' • ') : null,
  };
}
